import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './EncuestaCasualView.css';
import logoSura from './logo_sura_blanco.png';

// 15 preguntas sobre conocimiento y uso de IA en SURA
const SURVEY_QUESTIONS = [
  {
    id: 1,
    category: 'Conocimiento General',
    question: '¿Qué tan familiarizado estás con el concepto de Inteligencia Artificial?',
    options: [
      'No sé qué es',
      'He escuchado el término pero no lo entiendo bien',
      'Tengo un conocimiento básico',
      'Tengo un buen entendimiento',
      'Soy experto/a en el tema'
    ]
  },
  {
    id: 2,
    category: 'Conocimiento General',
    question: '¿Conoces herramientas de IA generativa como ChatGPT, Gemini o Claude?',
    options: [
      'No conozco ninguna',
      'He escuchado de ellas pero nunca las he usado',
      'Conozco algunas pero no las uso',
      'Conozco y uso algunas ocasionalmente',
      'Las conozco y uso frecuentemente'
    ]
  },
  {
    id: 3,
    category: 'Conocimiento General',
    question: '¿Sabes cuál es la diferencia entre IA generativa e IA tradicional?',
    options: [
      'No, no sé la diferencia',
      'Tengo una idea vaga',
      'Entiendo la diferencia básica',
      'Puedo explicar la diferencia claramente',
      'Soy experto/a y podría dar ejemplos detallados'
    ]
  },
  {
    id: 4,
    category: 'Conocimiento General',
    question: '¿Qué tanto conoces sobre la creación de un prompt para IA?',
    options: [
      'Nunca he escuchado ese término',
      'He escuchado el término pero no sé qué significa',
      'Tengo una idea general de lo que es',
      'Entiendo bien cómo funciona',
      'Tengo conocimiento técnico profundo'
    ]
  },
  {
    id: 5,
    category: 'Conocimiento General',
    question: '¿Conoces los riesgos de privacidad y seguridad asociados con la IA?',
    options: [
      'No conozco ningún riesgo',
      'He escuchado que hay riesgos pero no sé cuáles',
      'Conozco algunos riesgos básicos',
      'Estoy bien informado/a sobre los riesgos',
      'Soy experto/a en seguridad y privacidad de IA'
    ]
  },
  {
    id: 6,
    category: 'Uso Actual',
    question: '¿Has utilizado herramientas de IA en tu trabajo en SURA?',
    options: [
      'Nunca',
      'Una o dos veces por curiosidad',
      'Ocasionalmente cuando lo necesito',
      'Regularmente como parte de mi trabajo',
      'Diariamente, es esencial para mi trabajo'
    ]
  },
  {
    id: 7,
    category: 'Uso Actual',
    question: '¿Cuál herramienta de IA usas con más frecuencia?',
    options: [
      'No uso ninguna herramienta de IA',
      'ChatGPT (OpenAI)',
      'Gemini (Google)',
      'Claude (Anthropic)',
      'Microsoft Copilot',
      'Otra herramienta'
    ]
  },
  {
    id: 8,
    category: 'Uso Actual',
    question: '¿Para qué tareas usas principalmente la IA?',
    options: [
      'No uso IA para ninguna tarea',
      'Redacción y corrección de textos',
      'Análisis de datos e información',
      'Programación y desarrollo',
      'Investigación y aprendizaje',
      'Múltiples tareas variadas'
    ]
  },
  {
    id: 9,
    category: 'Uso Actual',
    question: '¿Tu equipo de trabajo utiliza herramientas de IA de manera colaborativa?',
    options: [
      'No, nadie en mi equipo usa IA',
      'Algunos compañeros las usan individualmente',
      'Usamos IA ocasionalmente en proyectos',
      'Tenemos prácticas establecidas para usar IA',
      'La IA está integrada en nuestros procesos diarios'
    ]
  },
  {
    id: 10,
    category: 'Uso Actual',
    question: '¿Has recibido capacitación sobre IA?',
    options: [
      'No, nunca he recibido capacitación',
      'No, pero me gustaría recibirla',
      'Sí, una capacitación básica',
      'Sí, varias capacitaciones',
      'Sí, y también me capacito por mi cuenta'
    ]
  },
  {
    id: 11,
    category: 'Percepción y Futuro',
    question: '¿Crees que la IA puede mejorar tu productividad en el trabajo?',
    options: [
      'No, no creo que me ayude',
      'Tal vez en algunas tareas menores',
      'Sí, en varias de mis tareas',
      'Definitivamente, en la mayoría de mis tareas',
      'Ya ha mejorado significativamente mi productividad'
    ]
  },
  {
    id: 12,
    category: 'Percepción y Futuro',
    question: '¿Te preocupa que la IA pueda afectar tu trabajo en el futuro?',
    options: [
      'Sí, me preocupa mucho',
      'Tengo algunas preocupaciones',
      'Estoy neutral al respecto',
      'No me preocupa, veo oportunidades',
      'No me preocupa, sé cómo adaptarme'
    ]
  },
  {
    id: 13,
    category: 'Percepción y Futuro',
    question: '¿Qué tan interesado/a estás en aprender más sobre IA?',
    options: [
      'No me interesa',
      'Tengo poco interés',
      'Tengo interés moderado',
      'Estoy muy interesado/a',
      'Es una prioridad para mi desarrollo profesional'
    ]
  },
  {
    id: 14,
    category: 'Percepción y Futuro',
    question: '¿Qué área de IA te interesa más explorar?',
    options: [
      'Ninguna en particular',
      'Asistentes de escritura y comunicación',
      'Análisis de datos y reportes',
      'Automatización de tareas repetitivas',
      'Creación de contenido (imágenes, videos)',
      'Programación y desarrollo de software'
    ]
  },
  {
    id: 15,
    category: 'Percepción y Futuro',
    question: '¿Recomendarías el uso de herramientas de IA a tus colegas?',
    options: [
      'No, no las recomendaría',
      'Solo si tienen el conocimiento necesario',
      'Sí, con precaución y supervisión',
      'Sí, son muy útiles',
      'Absolutamente, son indispensables hoy en día'
    ]
  }
];

const EncuestaCasualView = () => {
  const [currentStep, setCurrentStep] = useState(-1); // -1 = pantalla de bienvenida
  const [answers, setAnswers] = useState({});
  const [isComplete, setIsComplete] = useState(false);

  const totalQuestions = SURVEY_QUESTIONS.length;
  const progress = currentStep >= 0 ? ((currentStep + 1) / totalQuestions) * 100 : 0;

  const handleStart = () => {
    setCurrentStep(0);
  };

  const handleAnswer = (questionId, answer) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };

  const handleNext = () => {
    if (currentStep < totalQuestions - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      setIsComplete(true);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleRestart = () => {
    setCurrentStep(-1);
    setAnswers({});
    setIsComplete(false);
  };

  const currentQuestion = currentStep >= 0 ? SURVEY_QUESTIONS[currentStep] : null;
  const isAnswered = currentQuestion && answers[currentQuestion.id] !== undefined;

  return (
    <div className="survey-container">
      {/* Fondo animado */}
      <div className="animated-background">
        <div className="gradient-orb orb-1"></div>
        <div className="gradient-orb orb-2"></div>
        <div className="gradient-orb orb-3"></div>
        <div className="gradient-orb orb-4"></div>
        <div className="mesh-overlay"></div>
      </div>

      {/* Partículas flotantes */}
      <div className="particles">
        {[...Array(20)].map((_, i) => (
          <div key={i} className={`particle particle-${i + 1}`}></div>
        ))}
      </div>

      {/* Contenido principal */}
      <div className="survey-content">
        <AnimatePresence mode="wait">
          {/* Pantalla de bienvenida */}
          {currentStep === -1 && !isComplete && (
            <motion.div
              key="welcome"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
              className="survey-card welcome-card"
            >
              <div className="welcome-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
                </svg>
              </div>
              <h1 className="welcome-title">Encuesta sobre Inteligencia Artificial</h1>
              <p className="welcome-subtitle">
                Queremos conocer tu experiencia y percepción sobre la IA en tu día a día laboral.
                Esta encuesta es anónima y toma aproximadamente 3 minutos.
              </p>
              <div className="welcome-stats">
                <div className="stat-item">
                  <span className="stat-number">15</span>
                  <span className="stat-label">Preguntas</span>
                </div>
                <div className="stat-divider"></div>
                <div className="stat-item">
                  <span className="stat-number">~3</span>
                  <span className="stat-label">Minutos</span>
                </div>
              </div>
              <button className="start-button" onClick={handleStart}>
                Comenzar
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                </svg>
              </button>
            </motion.div>
          )}

          {/* Preguntas */}
          {currentStep >= 0 && !isComplete && currentQuestion && (
            <motion.div
              key={`question-${currentStep}`}
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              transition={{ duration: 0.4, ease: 'easeOut' }}
              className="survey-card question-card"
            >
              {/* Barra de progreso */}
              <div className="progress-container">
                <div className="progress-info">
                  <span className="progress-category">{currentQuestion.category}</span>
                  <span className="progress-count">{currentStep + 1} / {totalQuestions}</span>
                </div>
                <div className="progress-bar">
                  <motion.div 
                    className="progress-fill"
                    initial={{ width: 0 }}
                    animate={{ width: `${progress}%` }}
                    transition={{ duration: 0.5, ease: 'easeOut' }}
                  ></motion.div>
                </div>
              </div>

              {/* Pregunta */}
              <h2 className="question-text">{currentQuestion.question}</h2>

              {/* Opciones */}
              <div className="options-container">
                {currentQuestion.options.map((option, index) => (
                  <motion.button
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className={`option-button ${answers[currentQuestion.id] === option ? 'selected' : ''}`}
                    onClick={() => handleAnswer(currentQuestion.id, option)}
                  >
                    <span className="option-indicator">
                      {answers[currentQuestion.id] === option ? (
                        <svg viewBox="0 0 24 24" fill="currentColor">
                          <path fillRule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm13.36-1.814a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z" clipRule="evenodd" />
                        </svg>
                      ) : (
                        <span className="option-circle"></span>
                      )}
                    </span>
                    <span className="option-text">{option}</span>
                  </motion.button>
                ))}
              </div>

              {/* Navegación */}
              <div className="navigation-buttons">
                <button 
                  className="nav-button prev-button"
                  onClick={handlePrevious}
                  disabled={currentStep === 0}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
                  </svg>
                  Anterior
                </button>
                <button 
                  className="nav-button next-button"
                  onClick={handleNext}
                  disabled={!isAnswered}
                >
                  {currentStep === totalQuestions - 1 ? 'Finalizar' : 'Siguiente'}
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                  </svg>
                </button>
              </div>
            </motion.div>
          )}

          {/* Pantalla de finalización */}
          {isComplete && (
            <motion.div
              key="complete"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.5 }}
              className="survey-card complete-card"
            >
              <div className="complete-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 01-1.043 3.296 3.745 3.745 0 01-3.296 1.043A3.745 3.745 0 0112 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 01-3.296-1.043 3.745 3.745 0 01-1.043-3.296A3.745 3.745 0 013 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 011.043-3.296 3.746 3.746 0 013.296-1.043A3.746 3.746 0 0112 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 013.296 1.043 3.746 3.746 0 011.043 3.296A3.745 3.745 0 0121 12z" />
                </svg>
              </div>
              <h1 className="complete-title">¡Gracias por participar!</h1>
              <p className="complete-subtitle">
                Tu opinión es muy valiosa para entender cómo podemos mejorar 
                la adopción de IA en SURA. Los resultados serán compartidos próximamente.
              </p>
              <div className="complete-summary">
                <div className="summary-item">
                  <span className="summary-icon">✓</span>
                  <span>15 preguntas completadas</span>
                </div>
              </div>
              <button className="restart-button" onClick={handleRestart}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
                </svg>
                Realizar de nuevo
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Logo SURA */}
      <div className="survey-footer">
        <img 
          src={logoSura} 
          alt="SURA" 
          className="footer-logo"
        />
        <span className="footer-text">SURA de Venezuela</span>
      </div>
    </div>
  );
};

export default EncuestaCasualView;
